import React, { useEffect, useMemo, useState } from 'react'
import { Tooltip } from '@ui-kitten/components'
import { TouchableOpacity, View, useWindowDimensions } from 'react-native'
import { useDispatch } from 'react-redux'
import { RouteProp, useRoute } from '@react-navigation/native'
import { NoraVoiceModeProvider } from '@screens/Nora/context/NoraVoiceModeContext'
import { AppStackParamList } from '@src/navigation/types'
import { NavigationContainer } from '@screens/Common/containers'
import { ChatScreen as ChatScreenComponent } from '@screens/Chat/components/ChatScreen'
import { Icon, Text } from '@components/base'
import { useDispatchAsync, useSnack } from '@utils'
import { StyleService, useStyleSheet } from '@style/service'
import { useStorageValue } from '@src/utils/storage'
import { Storage } from '@src/utils'
import { useGoBack } from '@src/utils/navigation'
import { ChatbotConversation } from '@src/types'
import { NSLoadingIndicator } from '@components'
import { ConversationTitle } from '@screens/Nora/components/ConversationTitle'
import { NoraFileAttachmentsProvider } from '@screens/Nora/context/NoraFileAttachmentsContext'
import { useNoraStreamChat } from '@screens/Nora/context/NoraStreamChatContext'
import { useSecureScreen } from '@src/hooks/useSecureScreen'
import { useAiProcessingConsentGate } from '@src/screens/PolicyConsents/hooks/useAiProcessingConsentGate'
import {
  NoraVoiceModeBackground,
  NoraVoiceModeRuntime,
} from '@screens/Nora/components/VoiceMode/NoraVoiceModeRuntime'

export const NoraConversationScreen = () => {
  useSecureScreen()

  const styles = useStyleSheet(themedStyle)
  const dispatch = useDispatch()
  const showSnack = useSnack()
  const goBack = useGoBack()
  const route = useRoute<RouteProp<AppStackParamList, 'NoraConversationScreen'>>()
  const params = route.params
  const initialConversation = params && 'conversation' in params ? params.conversation : undefined
  const initialPrompt = params && 'initialPrompt' in params ? params.initialPrompt : undefined
  const deepLinkProviderId =
    params && 'conversationProviderId' in params ? params.conversationProviderId : undefined
  const initialVoiceModeEnabled =
    params && 'initialVoiceModeEnabled' in params ? params.initialVoiceModeEnabled ?? false : false
  const initialVoiceModeSource =
    params && 'initialVoiceModeSource' in params ? params.initialVoiceModeSource : undefined

  const {
    client,
    channel,
    closeChannel,
    connectToConversation,
    setShouldReloadConversations,
  } = useNoraStreamChat()
  const dispatchAsync = useDispatchAsync()
  const [conversation, setConversation] = useState(initialConversation)
  const [scrollTarget, setScrollTarget] = useState<{ messageId?: string } | null>(() => {
    // No anchoring needed when there's nothing to anchor against (onAsk flow) or when
    // the conversation passed via params is already read.
    if (!initialConversation && !deepLinkProviderId) {
      return {}
    }
    if (initialConversation?.read) {
      return {}
    }

    // Unread params flow: openConversation awaits channel.watch() before navigating, so
    // the channel and its messages are already in context at first render.
    if (initialConversation && channel && channel.state.messages.length > 0) {
      return { messageId: channel.state.messages[0].id }
    }

    // Wait — deep-link conversation fetch pending, or channel not yet in context.
    return null
  })

  if (scrollTarget === null && channel) {
    const knownConversation = initialConversation ?? conversation
    const waitingForDeepLinkConversation = !knownConversation && !!deepLinkProviderId
    if (!waitingForDeepLinkConversation) {
      setScrollTarget(
        knownConversation && !knownConversation.read && channel.state.messages.length > 0
          ? { messageId: channel.state.messages[0].id }
          : {},
      )
    }
  }

  const handleGoBack = () => {
    closeChannel()
    goBack()
  }

  // Covers deep links and any other entry that bypasses NoraStreamChatContext. Leaving the consent
  // screen without accepting leaves the conversation.
  const { consented } = useAiProcessingConsentGate({ onDeclined: handleGoBack })

  const activeProviderId = deepLinkProviderId ?? channel?.id

  useEffect(() => {
    // Deep links bypass NoraStreamChatContext's consent check, so don't connect until consented.
    if (!consented) {
      return
    }

    if (deepLinkProviderId && (!channel || channel.id !== deepLinkProviderId)) {
      connectToConversation(deepLinkProviderId).catch(() => undefined)
    }

    if (activeProviderId && !conversation) {
      dispatchAsync<ChatbotConversation>({
        type: 'nora/chatbotConversation',
        payload: { providerId: activeProviderId },
      })
        .then(setConversation)
        .catch(() => undefined)
    }
  }, [
    consented,
    activeProviderId,
    deepLinkProviderId,
    channel,
    conversation,
    connectToConversation,
    dispatchAsync,
  ])

  const [tooltipShown, setTooltipShown] = useStorageValue(
    `${Storage.NORA_PIN_CONVERSATION_TOOLTIP_SHOWN_KEY}_${client.user?.id}`,
  )
  const { width: screenWidth } = useWindowDimensions()

  useEffect(() => {
    if (conversation && !conversation.read) {
      dispatch({
        type: 'nora/updateChatbotConversation',
        payload: { id: conversation.id, read: true },
        success: (updated) => setConversation(updated),
      })
    }
  }, [conversation, dispatch])

  const rightAccessories = useMemo(() => {
    if (!conversation?.id && !channel?.id) {
      return []
    }

    const togglePinned = () => {
      dispatch({
        type: 'nora/updateChatbotConversation',
        payload: {
          id: conversation?.id,
          providerId: channel?.id,
          pinned: !conversation?.pinned,
        },
        success: (updatedConversation) => {
          setConversation(updatedConversation)
          // Pinning/unpinning changes the Home screen's pinned preview/count,
          // which live in a separate slice; refresh them on next focus.
          setShouldReloadConversations(true)
        },
        failure: (error: Error) => {
          showSnack(error.message, null, 'error')
        },
      })
    }

    return [
      <Tooltip
        key="pin"
        placement="bottom"
        visible={!tooltipShown}
        onBackdropPress={() => {
          setTooltipShown(true)
        }}
        anchor={() => (
          <TouchableOpacity
            accessibilityLabel="Pin/Unpin Conversation"
            onPress={togglePinned}
            style={styles.pinIcon}
          >
            <Icon name="push-pin" size={24} weight={conversation?.pinned ? 'fill' : 'regular'} />
          </TouchableOpacity>
        )}
      >
        {() => (
          <View style={styles.tooltipContent}>
            <TouchableOpacity
              accessibilityLabel="Dismiss tooltip"
              onPress={() => setTooltipShown(true)}
            >
              <Icon name="x" size="12" weight="bold" style={styles.tooltipClose} />
            </TouchableOpacity>
            <Text type="small" style={styles.tooltipText}>
              Pin conversations for easy access later.
            </Text>
          </View>
        )}
      </Tooltip>,
    ]
  }, [
    conversation,
    channel?.id,
    tooltipShown,
    dispatch,
    showSnack,
    setShouldReloadConversations,
    setTooltipShown,
    styles.pinIcon,
    styles.tooltipContent,
    styles.tooltipClose,
    styles.tooltipText,
  ])

  return (
    <NavigationContainer
      title={<View />}
      goBack={handleGoBack}
      style={styles.container}
      leftIcon={
        conversation ? (
          <View style={[styles.leftIcon, { maxWidth: screenWidth - 80 }]}>
            <Icon name="arrow-left" size="24" weight="bold" />
            <ConversationTitle conversation={conversation} />
          </View>
        ) : undefined
      }
      rightAccessories={rightAccessories}
      navigationBarProps={{ contentStyle: styles.navBarContent }}
    >
      {consented && channel && scrollTarget ? (
        <NoraVoiceModeProvider
          initialEnabled={initialVoiceModeEnabled}
          initialSource={initialVoiceModeSource}
        >
          <NoraFileAttachmentsProvider>
            <NoraVoiceModeRuntime conversationId={conversation?.id} />
            {/*
             * NavigationContainer paints the solid base that replaces the chat's
             * own transparent background. ChatScreen paints the Voice Mode wave
             * before the transparent Stream tree so the existing z-order is preserved.
             */}
            <ChatScreenComponent
              client={client}
              channel={channel}
              initialPrompt={initialPrompt}
              initialMessageId={scrollTarget.messageId}
              background={<NoraVoiceModeBackground />}
              transparentBackground
            />
          </NoraFileAttachmentsProvider>
        </NoraVoiceModeProvider>
      ) : (
        <View style={styles.loadingContainer}>
          <NSLoadingIndicator />
        </View>
      )}
    </NavigationContainer>
  )
}

const themedStyle = StyleService.create({
  container: {
    backgroundColor: 'theme.background',
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pinIcon: {
    marginRight: 24,
  },
  leftIcon: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    marginLeft: 16,
  },
  navBarContent: {
    height: 60,
  },
  tooltipContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  tooltipClose: {
    color: 'theme.tooltip.textPrimary',
  },
  tooltipText: {
    color: 'theme.tooltip.textPrimary',
  },
})
